import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Zap, Star, Check, Sparkles, Gift, Truck, Clock, HeadphonesIcon, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier card configurations - returns translated config
const getTierConfig = (t) => ({
  free: {
    name: t('home.membership.freeTier'),
    icon: Crown,
    color: "emerald",
    gradient: "from-emerald-500/20 to-emerald-600/10",
    borderColor: "border-emerald-500/30",
    textColor: "text-emerald-400",
    price: { monthly: 0, yearly: 0 },
    tagline: t('home.membership.freeTagline'),
    features: [
      { icon: Gift, text: t('home.membership.benefits.discount15') },
      { icon: Sparkles, text: t('home.membership.benefits.earnPoints') },
      { icon: Check, text: t('home.membership.benefits.accessProducts') },
      { icon: Check, text: t('home.membership.benefits.newsletter') }
    ]
  },
  pro: {
    name: t('home.membership.proTier'),
    icon: Zap,
    color: "amber",
    gradient: "from-amber-500/20 to-amber-600/10",
    borderColor: "border-amber-500/30",
    textColor: "text-amber-400",
    price: { monthly: 4.99, yearly: 49 },
    tagline: t('home.membership.proTagline'),
    popular: true,
    features: [
      { icon: Gift, text: t('home.membership.benefits.discount20') },
      { icon: Sparkles, text: t('home.membership.benefits.2xPoints') },
      { icon: Truck, text: t('home.membership.benefits.freeShipping50') },
      { icon: Clock, text: t('home.membership.benefits.earlyAccess24') },
      { icon: ShieldCheck, text: t('home.membership.benefits.proBadge') }
    ]
  },
  vip: {
    name: t('home.membership.vipTier'),
    icon: Star,
    color: "violet",
    gradient: "from-violet-500/20 to-violet-600/10",
    borderColor: "border-violet-500/30",
    textColor: "text-violet-400",
    price: { monthly: 9.99, yearly: 99 },
    tagline: t('home.membership.vipTagline'),
    features: [
      { icon: Gift, text: t('home.membership.benefits.discount25') },
      { icon: Sparkles, text: t('home.membership.benefits.3xPoints') },
      { icon: Truck, text: t('home.membership.benefits.freeShippingAlways') },
      { icon: Clock, text: t('home.membership.benefits.earlyAccess48') },
      { icon: HeadphonesIcon, text: t('home.membership.benefits.prioritySupport') },
      { icon: Star, text: t('home.membership.benefits.vipBadge') }
    ]
  }
});

// Single Tier Card Component
const TierCard = ({ tier, config, isCurrentTier, onSelect, billingCycle, t }) => {
  const Icon = config.icon;
  const price = billingCycle === "yearly" ? config.price.yearly : config.price.monthly;
  const period = billingCycle === "yearly" ? "/year" : t('home.membership.perMonth');
  const savings = billingCycle === "yearly" && config.price.monthly > 0 
    ? Math.round(100 - (config.price.yearly / (config.price.monthly * 12) * 100))
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative flex flex-col p-6 lg:p-8 rounded-2xl border bg-gradient-to-b ${config.gradient} ${config.borderColor} backdrop-blur-sm ${config.popular ? 'ring-2 ring-amber-500/50' : ''}`}
    >
      {/* Popular Badge */}
      {config.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 text-xs font-semibold bg-amber-500 text-black rounded-full">
            {t('home.membership.mostPopular')}
          </span>
        </div>
      )}

      {/* Tier Header */}
      <div className="text-center mb-6">
        <div className={`inline-flex p-3 rounded-xl bg-${config.color}-500/20 mb-4`}>
          <Icon className={`h-8 w-8 ${config.textColor}`} />
        </div>
        <h3 className="text-2xl font-bold text-white mb-1">{config.name}</h3>
        <p className="text-sm text-zinc-400">{config.tagline}</p>
      </div>

      {/* Price */}
      <div className="text-center mb-6">
        {price === 0 ? (
          <div className="text-4xl font-bold text-white">{t('home.membership.free')}</div>
        ) : (
          <>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-lg text-zinc-400">£</span>
              <span className="text-4xl font-bold text-white">{price}</span>
              <span className="text-zinc-400">{period}</span>
            </div>
            {savings > 0 && (
              <p className="text-sm text-emerald-400 mt-1">{t('home.membership.saveUpTo')}</p>
            )}
          </>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-grow">
        {config.features.map((feature, idx) => (
          <li key={idx} className="flex items-center gap-3">
            <feature.icon className={`h-5 w-5 ${config.textColor} flex-shrink-0`} />
            <span className="text-sm text-zinc-300">{feature.text}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      {tier === "free" ? (
        <Link to="/register" className="w-full">
          <Button
            className={`w-full py-3 bg-${config.color}-500/20 hover:bg-${config.color}-500/30 ${config.textColor} border ${config.borderColor}`}
            variant="outline"
          >
            {isCurrentTier ? "Current Plan" : t('home.membership.signUpFree')}
          </Button>
        </Link>
      ) : (
        <Button
          onClick={() => onSelect(tier, billingCycle)}
          disabled={isCurrentTier}
          className={`w-full py-3 ${isCurrentTier 
            ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed' 
            : tier === 'pro' 
              ? 'bg-amber-500 hover:bg-amber-600 text-black font-semibold' 
              : 'bg-violet-500 hover:bg-violet-600 text-white font-semibold'
          }`}
        >
          {isCurrentTier ? "Current Plan" : tier === 'pro' ? t('home.membership.upgradeToPro') : t('home.membership.upgradeToVip')}
        </Button>
      )}
    </motion.div>
  );
};

// Main Membership Section Component
export default function MembershipSection({ showTitle = true }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get translated tier config
  const TIER_CONFIG = getTierConfig(t);

  // Check current user's membership
  useEffect(() => {
    const checkMembership = async () => {
      const token = localStorage.getItem("alchemist_customer_token");
      const customer = localStorage.getItem("alchemist_customer");
      
      if (token && customer) {
        try {
          const parsed = JSON.parse(customer);
          setCurrentTier(parsed.membership_tier || "free");
        } catch (e) {
          console.error("Failed to parse customer data");
        }
      }
    };
    checkMembership();
  }, []);

  const handleSelectTier = async (tier, cycle) => {
    const token = localStorage.getItem("alchemist_customer_token");
    const customer = localStorage.getItem("alchemist_customer");
    
    if (!token || !customer) {
      toast.info("Please sign in to upgrade your membership");
      navigate("/register");
      return;
    }

    setLoading(true);
    try {
      const parsed = JSON.parse(customer);
      const response = await axios.post(
        `${API_URL}/api/membership/subscribe?customer_id=${parsed.id}`,
        {
          tier,
          billing_cycle: cycle,
          success_url: `${window.location.origin}/account?tab=membership&success=true`,
          cancel_url: `${window.location.origin}/account?tab=membership`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error("Failed to start subscription:", err);
      toast.error("Failed to start subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden bg-[#050505]" data-testid="membership-section">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        {showTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <Crown className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-400">{t('home.membership.eyebrow')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('home.membership.title')}
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {t('home.membership.subtitle')}
            </p>
          </motion.div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center p-1 bg-zinc-900 rounded-full border border-zinc-800">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-amber-500 text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {t('home.membership.monthly')}
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === "yearly"
                  ? "bg-amber-500 text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {t('home.membership.yearly')}
              <span className="ml-2 text-xs text-emerald-400">{t('home.membership.saveUpTo')}</span>
            </button>
          </div>
        </div>

        {/* Tier Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {Object.entries(TIER_CONFIG).map(([tier, config]) => (
            <TierCard
              key={tier}
              tier={tier}
              config={config}
              isCurrentTier={currentTier === tier}
              onSelect={handleSelectTier}
              billingCycle={billingCycle}
              t={t}
            />
          ))}
        </div>

        {/* Trust Badge & Cancellation Policy */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12 space-y-2"
        >
          <p className="text-sm text-zinc-500">
            <ShieldCheck className="h-4 w-4 inline mr-2" />
            {t('home.membership.cancelAnytime')}
          </p>
          <p className="text-xs text-zinc-600">
            {t('home.membership.cancelNote')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
